<template>
  <v-content class="pa-0">
    <v-card-title class="grey lighten-3">
      <v-row>
        <v-icon class="draggable">mdi-drag</v-icon>
        <v-col class="py-0">{{ taxLabel }}</v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pa-3">
      <v-container fluid grid-list-md class="pa-0">
        <v-layout row wrap>
          <v-flex sm12>
            <v-text-field
              label="Description"
              v-model="t.description"
              @input="$v.t.description.$touch()"
              @blur="$v.t.description.$touch()"
              :error-messages="descriptionErrors"
              :outlined="outlinedInputs"
            ></v-text-field>
          </v-flex>
          <v-flex sm6>
            <dates-range-picker
              :gt-date-from-string="validFrom"
              :gt-date-to-string="validTo"
              :outlined="outlinedInputs"
              @onRangeSelected="onDatesChange"
            ></dates-range-picker>
          </v-flex>

          <v-flex sm3>
            <v-text-field
              label="Code"
              v-model="t.code"
              @input="$v.t.code.$touch()"
              @blur="$v.t.code.$touch()"
              :error-messages="codeErrors"
              :outlined="outlinedInputs"
            ></v-text-field>
          </v-flex>
          <v-flex sm3>
            <v-text-field
              label="Percentage"
              v-model.number="t.percentage"
              suffix="%"
              @input="$v.t.percentage.$touch()"
              @blur="$v.t.percentage.$touch()"
              :error-messages="percentageErrors"
              :outlined="outlinedInputs"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex sm4>
            <v-checkbox
              label="Accommodation"
              v-model="t.isAccommodation"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex sm4>
            <v-checkbox
              label="Activity"
              v-model="t.isActivity"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex sm4>
            <v-checkbox
              label="Transport"
              v-model="t.isTransport"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex sm4>
            <v-checkbox
              label="Travel"
              v-model="t.isTravel"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex sm4>
            <v-checkbox
              label="Other"
              v-model="t.isOther"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex sm4>
            <v-checkbox
              label="Transfer"
              v-model="t.isTransfer"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>
    <div v-if="debug">{{ t }}</div>
  </v-content>
</template>

<script>
import {
  TAX_UPDATE_MUTATION,
  TAX_CREATE_MUTATION,
} from "../../../../constants/graphql";
import { NumberValidator } from "../../../../helpers/validators/simple-validators";
import { required } from "vuelidate/lib/validators";
import DatesRangePicker from "../../shared/DatesRangePicker.vue";

export default {
  components: { DatesRangePicker },
  name: "TaxesEditor",

  props: {
    tax: {
      type: Object,
      default: () => ({}),
    },

    doSaveTax: {
      type: Boolean,
      default: false,
    },

    taxEditorDialog: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      t: { ...this.tax },
      debug: false,
      validFrom: this.tax && this.tax.validFrom ? this.tax.validFrom : "",
      validTo: this.tax && this.tax.validTo ? this.tax.validTo : "",
      selectedTaxType: "",
    };
  },

  methods: {
    saveTax() {
      this.$emit("onResetSave");

      this.$v.$touch();
      if (!this.$v.$invalid) {
        let mutation =
          this.context == "edit" ? TAX_UPDATE_MUTATION : TAX_CREATE_MUTATION;
        this.$apollo
          .mutate({
            mutation: mutation,
            variables: {
              input: {
                description: this.t.description,
                code: this.t.code,
                percentage: this.t.percentage,
                isAccommodation: this.t.isAccommodation,
                isActivity: this.t.isActivity,
                isTransport: this.t.isTransport,
                isTravel: this.t.isTravel,
                isOther: this.t.isOther,
                isTransfer: this.t.isTransfer,
                validFrom: this.validFrom != "" ? this.validFrom : undefined,
                validTo: this.validTo != "" ? this.validTo : undefined,
              },
              taxId: this.context == "edit" ? this.tax.id : undefined,
            },
          })
          .then(this.saveGqlObjectHandler, this.saveGqlObjectErrorHandler);
      }
    },
    saveGqlObjectHandler(result) {
      this.$emit("saved", this.context);
    },
    saveGqlObjectErrorHandler(err) {
      console.log(err);
    },
    onSelectTaxType(data) {
      this.t.code = data.code;
      this.selectedTaxType = data.code;
      this.$v.selectedTaxType.$touch();
    },
    onDatesChange(range) {
      this.validFrom = range.dateFrom;
      this.validTo = range.dateTo;
    },
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    taxLabel() {
      return this.tax.id != null ? this.tax.description : "New Tax";
    },
    context() {
      return this.tax.id != null ? "edit" : "create";
    },
    descriptionErrors() {
      let errors = [];
      if (!this.$v.t.description.$dirty) {
        return errors;
      }
      if (!this.$v.t.description.required) {
        errors.push("Description is required.");
      }
      return errors;
    },
    codeErrors() {
      let errors = [];
      if (!this.$v.t.code.$dirty) {
        return errors;
      }
      if (!this.$v.t.code.required) {
        errors.push("Code is required.");
      }
      return errors;
    },
    percentageErrors() {
      let errors = [];
      if (!this.$v.t.percentage.$dirty) {
        return errors;
      }
      if (!this.$v.t.percentage.required) {
        errors.push("Percentage is required.");
      }
      if (!this.$v.t.percentage.NumberValidator) {
        errors.push("Percentage must be a number.");
      }
      return errors;
    },
  },

  watch: {
    tax(data) {
      let tax = { ...data };
      if (data.id != null) {
        delete tax.__typename;
        this.selectedTaxType = tax.code;
        this.validFrom = tax.validFrom ? tax.validFrom : "";
        this.validTo = tax.validTo ? tax.validTo : "";
      } else {
        tax.isAccommodation = false;
        tax.isActivity = false;
        tax.isTransport = false;
        tax.isTravel = false;
        tax.isOther = false;
        tax.isTransfer = false;
        this.validFrom = "";
        this.validTo = "";
      }
      this.t = tax;
    },
    taxEditorDialog(value) {
      if (!value) {
        this.t = {};
        this.selectedTaxType = "";
      }
      this.$v.$reset();
    },
    doSaveTax(value) {
      if (value) {
        this.saveTax();
      }
    },
  },

  validations: {
    t: {
      code: { required },
      percentage: { NumberValidator, required },
      description: { required },
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/admin/currencies/TaxEditor.vue